import React from 'react';

import { Pressable, ScrollView } from '@rbilabs/universal-components';
import Downshift from 'downshift';
import { Platform, StyleSheet, TextStyle, ViewStyle } from 'react-native';

import { Box, BoxProps, Typography, TypographyProps, withTypography } from 'components/ui';
import UnstyledButton from 'components/unstyled-button';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { ZIndex, getDesignToken, primitive, spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

import {
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator,
} from './mad-lib-select-accessories';
import { IFilter } from './types';

interface IIsOpen {
  isOpen: boolean;
}

const Container = ({ style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.container, style]} />
);

const SelectContainer = ({ isOpen, style, ...props }: BoxProps & IIsOpen) => {
  const isMobile = useIsMobileBreakpoint();

  return (
    <Box
      {...props}
      style={[
        styles.selectContainer,
        !isMobile && styles.selectContainerDesktop,
        { borderColor: isOpen ? getDesignToken('border-color-default') : 'transparent' },
        style,
      ]}
    />
  );
};

const SelectContent = ({ style, ...props }: BoxProps) => {
  const isMobile = useIsMobileBreakpoint();

  return (
    <Box
      {...props}
      style={[styles.selectContent, !isMobile && styles.selectContentDesktop, style]}
    />
  );
};

type VisibilityStyle = ViewStyle & {
  visibility?: 'hidden' | 'visible';
};

const BlackBar = ({ isOpen, style, ...props }: BoxProps & IIsOpen) => {
  const isMobile = useIsMobileBreakpoint();
  const visibilityStyle = {
    visibility: isOpen ? 'hidden' : 'visible',
  } as VisibilityStyle;

  return (
    <Box
      {...props}
      style={[styles.blackBar, !isMobile && styles.blackBarDesktop, visibilityStyle, style]}
    />
  );
};

const SelectToggle = UnstyledButton.withConfig({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flex: 1,
});

const SelectLabel = withTypography({
  mt: '$0',
  mr: '$0',
  mb: '$0',
  variant: 'copyOne',
  style: {
    textTransform: getDesignToken('text-transform-headlines') as any,
    letterSpacing: 1,
  },
});

const CurrentSelection = withTypography({
  variant: 'headerThreeLook',
  mb: '$0',
  color: getDesignToken('text-default'),
  style: {
    flex: 1,
    textTransform: getDesignToken('text-transform-headlines') as TextStyle['textTransform'],
    letterSpacing: 1,
  },
});

const MenuContainer = ({ isOpen, style, ...props }: BoxProps & IIsOpen) => (
  <Box
    {...props}
    style={[
      styles.menuContainer,
      Platform.OS === 'android' && { minHeight: isOpen ? spacing.$24 : 0 },
      style,
    ]}
  />
);

const MenuList = ScrollView.withConfig<IIsOpen>(p => ({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: ZIndex.BELOW,
  width: '100%',
  paddingY: p.isOpen ? '$1' : 0,
  backgroundColor: primitive.$white,
  borderBottomRightRadius: 8,
  borderBottomLeftRadius: 8,
  borderTopWidth: 0,
  maxHeight: {
    base: 200,
    md: 320,
  },
  borderWidth: '1',
  borderColor: p.isOpen ? getDesignToken('border-color-default') : 'transparent',
}));

const SelectItem = Pressable.withConfig<{
  isSelected: boolean;
  isHighlighted: boolean;
}>(p => ({
  backgroundColor: p.isSelected
    ? getDesignToken('background-active')
    : p.isHighlighted
    ? getDesignToken('background-focus')
    : null,
  _web: {
    cursor: 'pointer',
  },
  _hover: {
    background: !p.isSelected
      ? getDesignToken('background-focus')
      : getDesignToken('background-active'),
  },
  paddingX: {
    base: '$2',
    md: '$4',
  },
  paddingY: {
    base: '$1',
    md: '$1',
  },
}));

const SelectItemText = ({ isSelected, ...props }: TypographyProps & { isSelected: boolean }) => (
  <Typography
    variant="headerThree"
    m="$0"
    color={isSelected ? getDesignToken('text-reversed') : getDesignToken('text-default')}
    style={{
      textTransform: getDesignToken('text-transform-headlines') as TextStyle['textTransform'],
      letterSpacing: 1,
    }}
    {...props}
  />
);

const itemToString = (filter: IFilter | null) => filter?.description?.locale ?? '';

interface IMadLibSelect {
  prompt?: string;
  selectedFilter: IFilter | null;
  filters: IFilter[];
  onFilterChange: (filter?: IFilter | null) => void;
}

export const MadLibSelect: React.FC<React.PropsWithChildren<IMadLibSelect>> = ({
  onFilterChange,
  selectedFilter,
  filters,
  prompt,
}) => {
  return (
    <Downshift
      itemToString={itemToString}
      initialSelectedItem={selectedFilter ?? undefined}
      onChange={changes => onFilterChange(changes)}
    >
      {({
        getRootProps,
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
        clearSelection,
      }) => {
        const currentSelection = selectedItem
          ? selectedItem.description?.locale
          : t('selectAFilter');

        // Downshift appears bugged on web now and this fixes it. We should remove it anyways as its only used here and very heavy!
        const buttonProps = getToggleButtonProps();
        const onPress = buttonProps.onPress || buttonProps.onClick;

        return (
          <Container {...getRootProps({} as any, { suppressRefError: true })}>
            <SelectLabel
              {...getLabelProps()}
              {...hiddenAccessibilityPlatformProps}
              accessibilityRole="header"
            >
              {prompt || t('imCraving')}
            </SelectLabel>
            <SelectContainer isOpen={isOpen}>
              <SelectContent>
                <SelectToggle {...{ ...buttonProps, onPress }} accessibilityRole="button">
                  <CurrentSelection numberOfLines={1}>{currentSelection}</CurrentSelection>
                  <DropdownIndicator isOpen={isOpen} />
                </SelectToggle>
                <IndicatorSeparator />
                <ClearIndicator onPress={clearSelection} />
              </SelectContent>
              <BlackBar isOpen={isOpen} />
            </SelectContainer>
            <MenuContainer pointerEvents="auto" isOpen={isOpen}>
              <MenuList {...getMenuProps()} isOpen={isOpen}>
                {isOpen &&
                  filters.map((filter, index) => {
                    // Downshift appears bugged on web now and this fixes it. We should remove it anyways as its only used here and very heavy!
                    const itemProps = getItemProps({
                      item: filter,
                      index,
                    });
                    const onPress = itemProps.onPress || itemProps.onClick;

                    return (
                      <SelectItem
                        accessibilityRole="button"
                        key={filter._id}
                        isSelected={selectedItem?._id === filter._id}
                        isHighlighted={highlightedIndex === index}
                        {...{ ...itemProps, onPress }}
                      >
                        <SelectItemText isSelected={selectedItem?._id === filter._id}>
                          {filter.description?.locale}
                        </SelectItemText>
                      </SelectItem>
                    );
                  })}
              </MenuList>
            </MenuContainer>
          </Container>
        );
      }}
    </Downshift>
  );
};
export default MadLibSelect;

const styles = StyleSheet.create({
  blackBar: {
    backgroundColor: getDesignToken('icon-default'),
    height: 1,
    width: '100%',
  },
  blackBarDesktop: {
    height: 3,
  },
  container: {
    alignItems: 'stretch',
    minWidth: 0,
    width: '100%',
    zIndex: 2,
  },
  menuContainer: {
    width: '100%',
  },
  selectContainer: {
    borderBottomWidth: 0,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderWidth: 1,
    flexBasis: '100%',
    paddingHorizontal: spacing.$2,
  },
  selectContainerDesktop: {
    flexBasis: '80%',
    paddingHorizontal: spacing.$4,
  },
  selectContent: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingVertical: spacing.$2,
    width: '100%',
  },
  selectContentDesktop: {
    paddingVertical: spacing.$1,
  },
});
